import { AnalyticsMembershipEvent } from './AnalyticsMembershipEvent';
import { AnalyticsUserEvent } from './AnalyticsUserEvent';
import { BackendMembershipEvent } from './BackendMembershipEvent';
import { BackendUserEvent } from './BackendUserEvent';
import { PersistedUserEvent } from './PersistedUserEvent';

export type EventCatalogSource = 'user' | 'membership';
export type EventCatalogOrigin = 'member-app' | 'backend';

export interface EventMetadataField {
    type: 'string' | 'number' | 'boolean' | 'object' | 'object[]';
    optional?: boolean;
}

export interface EventCatalogEntry {
    /** Event name — what listeners put in `eventTypes`. */
    name: string;
    source: EventCatalogSource;
    origin: EventCatalogOrigin;
    /** `null` when no known properties are passed as `data`. */
    metadata: Record<string, EventMetadataField> | null;
}

const str: EventMetadataField = { type: 'string' };
const num: EventMetadataField = { type: 'number' };
const bool: EventMetadataField = { type: 'boolean' };
const objArr: EventMetadataField = { type: 'object[]' };
const optStr: EventMetadataField = { type: 'string', optional: true };
const optNum: EventMetadataField = { type: 'number', optional: true };
const optBool: EventMetadataField = { type: 'boolean', optional: true };

const moneyMapAppContext: Record<string, EventMetadataField> = {
    appContext: str,
};

const ddContext: Record<string, EventMetadataField> = {
    ddAppContext: str,
    ddSource: str,
};

const cmmSelectSubmitted: Record<string, EventMetadataField> = {
    responseKey: str,
    selectedOptionIds: str,
    hasCustomInput: bool,
    responseCustomInput: str,
};

const cmmConfirmation: Record<string, EventMetadataField> = {
    confirmationPrefix: str,
    messageCount: num,
};

const aiAgentRender: Record<string, EventMetadataField> = {
    messageId: str,
    messagePartIndex: num,
    messageCount: num,
    elapsedMsSinceLastUserMessage: optNum,
};

const aiOffTopic: Record<string, EventMetadataField> = {
    agentId: str,
    threadId: str,
    resourceId: str,
};

const guidanceSessionType: Record<string, EventMetadataField> = {
    guidanceSessionType: str,
};

/**
 * Known `data` property shapes from production call sites. Events omitted here
 * appear in the catalog with `metadata: null`.
 */
const METADATA_BY_NAME: Record<string, Record<string, EventMetadataField>> = {
    [AnalyticsUserEvent.VIEW]: { view: str },
    [AnalyticsUserEvent.BACK]: {
        pathname: optStr,
        flow: optStr,
        workflow: optStr,
    },
    [AnalyticsUserEvent.REFRESH_STALE_PAGE]: { pageIdleTime: num },
    [AnalyticsUserEvent.OPEN_MESSAGING]: { pathname: str },

    [AnalyticsUserEvent.OPEN_PREVIEW_MONEY_MAP]: moneyMapAppContext,
    [AnalyticsUserEvent.OPEN_COMPLETED_MONEY_MAP]: moneyMapAppContext,
    [AnalyticsUserEvent.OPEN_MONEY_MAP_WORKFLOW]: moneyMapAppContext,
    [AnalyticsUserEvent.OPEN_MONEY_MAP_GUIDE_PROMPT]: moneyMapAppContext,
    [AnalyticsUserEvent.MONEY_MAP_CHART_INTERACTION]: {
        action: str,
        filter: str,
    },
    [AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_TILE]: { step: str },
    [AnalyticsUserEvent.MONEY_MAP_WORKFLOW_START_CTA]: { mode: str },
    [AnalyticsUserEvent.MONEY_MAP_WORKFLOW_AUTOPAY_PROMPT]: { action: str },

    [AnalyticsUserEvent.INCOME_SPLIT_SUCCESS_INTERACTION]: { action: str },
    [AnalyticsUserEvent.INCOME_SPLIT_INCOME_TYPE_CHANGED]: { incomeType: str },
    [AnalyticsUserEvent.INCOME_SPLIT_ACCOUNTS_UPDATED]: {
        accountId: str,
        action: str,
    },
    [AnalyticsUserEvent.INCOME_SPLIT_DEPOSIT_DETAILS_CLICKED]: {
        executionId: str,
    },
    [AnalyticsUserEvent.INCOME_SPLIT_RULE_SET_EXPANDED]: { ruleSetName: str },

    [AnalyticsUserEvent.BANKING_ADOPTION_ITEM_TOGGLED]: {
        adoptionItem: str,
        adoptionItemMarkedComplete: bool,
        adoptionListContext: str,
    },
    [AnalyticsUserEvent.BANKING_ADOPTION_ITEM_CTA_PRESSED]: {
        adoptionItem: str,
        adoptionListContext: str,
    },
    [AnalyticsUserEvent.BANKING_ADOPTION_BILL_ITEM_TOGGLED]: {
        billMarkedAsSwitched: bool,
        billId: str,
        billCategory: str,
    },

    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_OPENED]: {
        ...ddContext,
        ddFlow: str,
    },
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_EXITED]: {
        ...ddContext,
        ddFlow: str,
    },
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_SUCCESS]: {
        ...ddContext,
        ddFlow: str,
        ddAccountId: str,
        ddPlatformId: str,
        ddParams: str,
    },
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_ERROR]: {
        ...ddContext,
        ddFlow: str,
        ddErrorType: str,
        ddErrorMessage: str,
        ddErrorCode: str,
        ddErrorPendingRetry: str,
    },
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_VIEW_MANUAL]: ddContext,
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_EXPLAINER_EXIT]: ddContext,
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_SELECT_ACCOUNT_EXIT]: ddContext,
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_ACCOUNT_SELECTED]: ddContext,
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_EXIT]: ddContext,
    [AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_DONE]: ddContext,

    [AnalyticsUserEvent.BENEFICIARY_ADDED]: { type: str },
    [AnalyticsUserEvent.ACCOUNT_VERIFICATION_LETTER_DOWNLOAD_ERROR]: {
        bankAccountId: str,
        errorMessage: str,
    },

    [AnalyticsUserEvent.CMM_SESSION_NEW]: {
        cmmStreamVersion: optStr,
        streamUrl: optStr,
        visitorId: optStr,
        resourceId: optStr,
    },
    [AnalyticsUserEvent.CMM_SESSION_RESTORED]: {
        messagesCount: num,
        visitorId: str,
        resourceId: str,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_INIT]: { visitorId: optStr },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_BRANCH_SELECTED]: { branch: str },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_CURRENT_STEP_UPDATED]: {
        currentStep: str,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_VIEW_FIRST_QUESTION]: {
        flowEntryVariant: optStr,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_ANSWER_FIRST_QUESTION]: {
        answerPayload: optStr,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_GO_TO_RESULTS]: {
        autoNavigate: bool,
        visitorId: str,
        threadId: str,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_RESULTS_SLIDE]: {
        slide: str,
        slideIndex: num,
    },
    [AnalyticsUserEvent.CMM_INPUT_MULTI_SELECT_SUBMITTED]: cmmSelectSubmitted,
    [AnalyticsUserEvent.CMM_INPUT_SINGLE_SELECT_SUBMITTED]: cmmSelectSubmitted,
    [AnalyticsUserEvent.CMM_INPUT_CONFIRMATION_CONFIRMED]: cmmConfirmation,
    [AnalyticsUserEvent.CMM_INPUT_CONFIRMATION_CHANGE_REQUESTED]:
        cmmConfirmation,
    [AnalyticsUserEvent.CMM_SPEND_GOALS_WIDGET_SLIDER]: {
        spendPercentage: num,
    },
    [AnalyticsUserEvent.CMM_SPEND_GOALS_CONFIRMED]: {
        monthlySpendCents: num,
        monthlyGoalsSpendCents: num,
    },
    [AnalyticsUserEvent.CMM_RESULTS_REVEALED]: { revealedBy: optStr },
    [AnalyticsUserEvent.CMM_RESULTS_PREV]: {
        fromSlide: optStr,
        fromSlideIndex: optNum,
    },
    [AnalyticsUserEvent.CMM_RESULTS_NEXT]: {
        fromSlide: optStr,
        fromSlideIndex: optNum,
    },
    [AnalyticsUserEvent.CMM_RESULTS_SLIDE_NEXT_BUTTON]: {
        fromSlide: str,
        fromSlideIndex: num,
    },
    [AnalyticsUserEvent.CMM_RESULTS_TOOLTIP_PRESS]: { tooltipId: str },
    [AnalyticsUserEvent.CMM_RESULTS_SPEND_GOALS_SLIDER]: {
        spendPercentage: num,
    },
    [AnalyticsUserEvent.CMM_RESULTS_INVEST_CONTRIB_SLIDER]: {
        monthlyContributionCents: num,
    },
    [AnalyticsUserEvent.CMM_RESULTS_INVEST_TIME_SLIDER]: {
        projectedYears: num,
    },
    [AnalyticsUserEvent.CMM_RESULTS_THREAD_SAVE_ERROR]: {
        visitorId: optStr,
        threadId: str,
        errorMessage: str,
    },
    [AnalyticsUserEvent.CMM_BUILDING_PLAN_STUCK_RECOVERY]: {
        messageCount: num,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_RESULTS_SUBMIT]: {
        threadId: str,
        visitorId: str,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_SUBSCRIPTION_STARTED]: {
        email: str,
        stripeCustomerId: str,
        guideSignUpId: str,
        membershipType: str,
        eventContext: str,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_GET_APP_PROMPT]: {
        downloadClicked: bool,
    },
    [AnalyticsUserEvent.SIGNUP_FUNNEL_SURVEY_EMAIL_SUBMIT]: { email: str },
    [AnalyticsUserEvent.MEMBERSHIP_PURCHASE_PUBLISHED]: {
        membershipPlanAmount: num,
        membershipEmail: str,
        stripeCustomerId: str,
    },

    [AnalyticsUserEvent.ONBOARDING_FUNNEL_BILLS_CONFIRMED]: {
        acceptedBillsCount: optNum,
    },
    [AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_CONFIRM_SHEET]: {
        isMembershipGuide: bool,
    },
    [AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_CONFIRM_PRESSED]: {
        daysFromNow: num,
        guideSwap: str,
    },
    [AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_VIEW_GUIDE_DETAILS]: {
        isMembershipGuide: bool,
    },
    [AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_AVAILABILITY_DATA]: {
        chunkIndex: num,
        queryWindowStart: str,
        queryWindowEnd: str,
        numberOfAvailableGuides: num,
        avgSessionsPerAvailableGuide: num,
        medianSessionsPerAvailableGuide: num,
        maxSessionsPerAvailableGuide: num,
        totalSessionsAvailableGuides: num,
        totalSessionsMembershipGuide: num,
    },
    [AnalyticsUserEvent.MAGIC_SAVE_DIDNT_BOOK_SCHEDULE]: {
        entryReason: str,
        detailReason: str,
    },

    [AnalyticsUserEvent.FTS_CHANGE_PERIOD]: { period: str },
    [AnalyticsUserEvent.FTS_TAP_TRANSACTION]: { view: optStr, period: optStr },

    [AnalyticsUserEvent.FRUITFUL_AI_CHAT_NEW_SESSION]: {
        resourceId: str,
        initialMessage: str,
    },
    [AnalyticsUserEvent.AI_CHAT_INPUT_TEXT_RESPONSE_SENT]: { inputLength: num },
    [AnalyticsUserEvent.AI_CHAT_USER_RESPONSE_RENDERED]: {
        messageId: str,
        messageCount: num,
        elapsedMsSinceLastAgentMessage: optNum,
    },
    [AnalyticsUserEvent.AI_CHAT_AGENT_RESPONSE_TEXT_RENDERED]: {
        ...aiAgentRender,
        messageLength: num,
    },
    [AnalyticsUserEvent.AI_CHAT_AGENT_RESPONSE_WIDGET_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
    },
    [AnalyticsUserEvent.AI_CHAT_SUGGESTED_RESPONSE_SELECTED]: { text: str },
    [AnalyticsUserEvent.AI_CHAT_SCROLLED_UP]: { scrollUpPx: num },
    [AnalyticsUserEvent.AI_CHAT_OFF_TOPIC_DETECTED]: {
        ...aiOffTopic,
        offTopicCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_OFF_TOPIC_CUTOFF_REACHED]: aiOffTopic,
    [AnalyticsUserEvent.AI_CHAT_SUPPORT_ESCALATION_DETECTED]: {
        ...aiOffTopic,
        trigger: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetTitle: str,
        widgetChartKind: str,
        widgetBarOrientation: str,
        widgetShowLegend: bool,
        widgetSeriesCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_SELECT]: {
        chartKind: str,
        chartSeriesCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_LEGEND_SELECT]: {
        chartKind: str,
        chartSeriesCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetTitle: str,
        widgetAccountName: str,
        widgetPeriod: str,
        widgetStatus: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_STATUS]: {
        status: str,
        period: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_TRANSACTIONS]: {
        status: str,
        period: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_TRANSACTIONS_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetTitle: str,
        widgetGroupBy: str,
        widgetGroupCount: num,
        widgetTransactionCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_TRANSACTIONS_OPEN]: {
        transactionType: str,
        transactionStatus: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_MONEY_MAP_FILTER]: { filter: str },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_ACCOUNT_BALANCES_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetTitle: str,
        widgetAccountCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_ACCOUNT_BALANCES_OPEN]: {
        accountName: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_INCOME_SPLIT_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetTitle: str,
        widgetMode: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_CARD_DETAILS_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetHasResolvedCard: bool,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_INLINE_DATA_TABLE_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetColumnCount: num,
        widgetRowCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_SPEND_GOALS_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetRemainingCents: num,
        widgetIsConfirmed: bool,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_MULTI_SELECT_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        widgetResponseKey: str,
        widgetOptionCount: num,
        widgetAllowCustomInput: bool,
        widgetPreSelectedCount: num,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_SEND_TO_GUIDE_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        messageBody: str,
    },
    [AnalyticsUserEvent.AI_CHAT_WIDGET_EMAIL_SUPPORT_RENDERED]: {
        ...aiAgentRender,
        messageWidgetType: str,
        subject: str,
        body: str,
    },

    [AnalyticsMembershipEvent.ALL_TASKS_COMPLETED_FOR_GOAL]: {
        goalId: str,
        goalCategory: str,
        goalCreatedAt: str,
    },
    [AnalyticsMembershipEvent.MONEY_MAP_WORKFLOW_COMPLETE]: {
        step_money_map_completed_on: str,
        steps_in_this_money_map: str,
        moneyMapId: str,
        moneyMapTitle: str,
        moneyMapCreatedAt: str,
    },
    [AnalyticsMembershipEvent.MEMBERSHIP_GUIDE_SWITCHED]: {
        originalGuideId: str,
        newGuideId: str,
    },
    [AnalyticsMembershipEvent.GUIDANCE_SESSION_SCHEDULED_VIA_CALENDLY]:
        guidanceSessionType,

    [BackendUserEvent.SUBSCRIPTION_CREATED]: {
        email: str,
        stripeCustomerId: str,
        subscriptionCreationOrigin: str,
        subscriptionHasPhases: bool,
        subscriptionProductId: str,
        membershipType: str,
        membershipTypeContext: str,
        billingFrequency: str,
        $device_id: str,
    },
    [BackendUserEvent.SUBSCRIPTION_CANCELED]: {
        stripeCustomerId: str,
        $device_id: str,
    },
    [BackendUserEvent.KYC_UPDATE]: { kycStatus: str },
    [BackendUserEvent.REFERRAL_PAYOUT_POSTED]: {
        referral_role: str,
        payout_type: str,
    },
    [BackendUserEvent.APEX_ACCOUNT_OPEN_DIGEST]: {
        accounts: objArr,
        accountsSummary: str,
        accountCount: num,
        userEmailBatchId: num,
        isFirstIMAccount: bool,
    },
    [BackendUserEvent.PASSWORD_CHANGED]: { method: str },

    [BackendMembershipEvent.MEMBERSHIP_PROFILE_COMPLETED]: {
        context: optStr,
    },
    [BackendMembershipEvent.GUIDANCE_SESSION_BOOKED]: guidanceSessionType,
    [BackendMembershipEvent.GUIDANCE_SESSION_BOOKED_VIA_API]:
        guidanceSessionType,
    [BackendMembershipEvent.FIRST_KICKOFF_SESSION_BOOKED]: {
        eventId: str,
        currency: str,
        value: num,
    },
    [BackendMembershipEvent.GUIDANCE_SESSION_CANCELED]: guidanceSessionType,
    [BackendMembershipEvent.GUIDANCE_SESSION_NO_SHOWED]: guidanceSessionType,
    [BackendMembershipEvent.MONEY_MAP_PUBLISHED_FOR_PREVIEW]: {
        moneyMapId: str,
        moneyMapTitle: str,
        moneyMapStatus: str,
    },
    [BackendMembershipEvent.MONEY_MAP_FINALIZED_FOR_ACTION]: {
        moneyMapId: str,
        moneyMapTitle: str,
        moneyMapStatus: str,
    },
    [BackendMembershipEvent.MONEY_MAP_EDITED_BY_MEMBER]: {
        moneyMapId: str,
        previousMoneyMapId: str,
        moneyMapTitle: str,
    },
    [BackendMembershipEvent.FIRST_NON_ZERO_PAYMENT]: {
        firstNonZeroPaymentAt: str,
    },
    [BackendMembershipEvent.MEMBER_SUBSCRIPTION_ACTIVATED]: {
        hasGuide: bool,
        membershipCreation: str,
        messagingSetup: str,
        cmmThreadLink: str,
        cmmThreadLinkSkipReason: str,
        referralLink: str,
        visitorId: optStr,
    },
    [BackendMembershipEvent.MEMBER_SIGNUP_COMPLETED]: {
        robottaMessage: str,
        cmmResultPopulation: str,
        cmmJourneyUpdate: str,
        cmmSkipReason: str,
        cmmMastraThreadId: optStr,
        visitorId: optStr,
    },
    [BackendMembershipEvent.MEMBERSHIP_SUBSCRIPTION_CHANGED]: {
        fromStatus: optStr,
        toStatus: str,
        fromProduct: optStr,
        toProduct: str,
        fromBillingFrequency: optStr,
        toBillingFrequency: str,
        hasPlusFeatures: bool,
        scheduleUpdated: optBool,
        source: str,
        adminUserEmail: optStr,
        memberUserId: optStr,
        liveProduct: optStr,
    },
    [BackendMembershipEvent.MEMBERSHIP_SUBSCRIPTION_CANCEL_SUBMIT]: {
        cancellationDate: str,
        cancellationReason: str,
        cancelPath: optStr,
        currentPhaseProduct: optStr,
    },
    [BackendMembershipEvent.MEMBERSHIP_SUBSCRIPTION_CANCEL_STOP]: {
        cancellationDate: optStr,
        cancellationReason: optStr,
        cancelPath: optStr,
        currentPhaseProduct: optStr,
        source: str,
    },
    [BackendMembershipEvent.ONBOARDING_FUNNEL_EMPLOYMENT_STATUS_COMPLETED]: {
        employmentStatus: str,
        pastData: optStr,
    },
    [BackendMembershipEvent.ONBOARDING_FUNNEL_INSURANCE_COMPLETED]: {
        insuranceCoverage: str,
        pastData: optStr,
    },
    [BackendMembershipEvent.ONBOARDING_FUNNEL_TAXES_COMPLETED]: {
        taxFiling: str,
        pastData: optStr,
    },
    [BackendMembershipEvent.ONBOARDING_FUNNEL_KIDS_COMPLETED]: { kids: str },
    [BackendMembershipEvent.CASH_ACCOUNT_TRANSFER_INITIATED]: {
        kind: str,
        amountCents: num,
        transactionUuid: optStr,
    },
    [BackendMembershipEvent.CASH_ACCOUNT_RDFI_TRANSFER_RECEIVED]: {
        kind: str,
        amountCents: num,
    },
    [BackendMembershipEvent.ACH_RETURN]: {
        amountCents: num,
        achReturnCode: str,
    },
};

/**
 * Extra `data` fields on UserEventService events, on top of request
 * `ipAddress` / `userAgent` which the service always attaches.
 */
const USER_EVENT_EXTRA: Partial<
    Record<PersistedUserEvent, Record<string, EventMetadataField>>
> = {
    [PersistedUserEvent.WEB_APP_LOGIN]: {
        isWeb: bool,
        isEmailVerified: bool,
        isPhoneOnStytch: bool,
        isUsingBiometrics: bool,
        userHasAuthedWithOtp: bool,
        isAuthenticatedPostSignup: bool,
        disableMfa: bool,
        sessionId: str,
        sessionStartedAt: str,
    },
    [PersistedUserEvent.MOBILE_APP_LOGIN]: {
        isWeb: bool,
        isEmailVerified: bool,
        isPhoneOnStytch: bool,
        isUsingBiometrics: bool,
        userHasAuthedWithOtp: bool,
        isAuthenticatedPostSignup: bool,
        disableMfa: bool,
        sessionId: str,
        sessionStartedAt: str,
    },
    [PersistedUserEvent.WEB_PLAID_EXCHANGE_LOGIN]: { fdxAuthGrantStatus: str },
    [PersistedUserEvent.SMS_CONSENT_OPT_IN]: {
        phoneNumber: str,
        disclosureText: str,
        platform: str,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_INVESTMENT_MANAGEMENT_AGREEMENT]:
        {
            description: optStr,
            url: optStr,
            version: optStr,
            syncteraDisclosures: objArr,
        },
    [PersistedUserEvent.INCOME_SPLIT_STATUS_CHANGE]: {
        incomeSplitConfigId: str,
        incomeSplitStatus: str,
    },
    [PersistedUserEvent.CARD_ACCOUNT_AUTOPAY_STATUS_CHANGE]: {
        autoPayStatus: str,
        syncteraAccountId: str,
        bankAccountId: optStr,
        appContext: optStr,
        disclosures: objArr,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_ACCOUNT_OPENING_CASH]: {
        disclosures: objArr,
        appContext: str,
        bankAccountId: optStr,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_ACCOUNT_OPENING_CARD]: {
        disclosures: objArr,
        appContext: str,
        bankAccountId: optStr,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_ACCOUNT_JOINING_CASH]: {
        disclosures: objArr,
        appContext: str,
        bankAccountId: optStr,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_ACCOUNT_JOINING_CARD]: {
        disclosures: objArr,
        appContext: str,
        bankAccountId: optStr,
    },
    [PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_ACCOUNT_OPENING_CASH_KYC]: {
        disclosures: objArr,
        appContext: str,
    },
    [PersistedUserEvent.DIRECT_DEPOSIT_ACCOUNT_VERIFICATION_LETTER_DOWNLOADED]:
        {
            bankAccountId: str,
        },
    [PersistedUserEvent.BILL_PAY_ACCOUNT_VERIFICATION_LETTER_DOWNLOADED]: {
        bankAccountId: str,
    },
    [PersistedUserEvent.SIGNUP_ONBOARDING_CONVERSION_EVENT_TRACKED]: {
        membershipId: str,
        eventDestinations: str,
        triggeredByEvent: str,
    },
};

const userEventRequestContext: Record<string, EventMetadataField> = {
    ipAddress: str,
    userAgent: str,
};

const toEventCatalogEntries = (
    names: string[],
    source: EventCatalogSource,
    origin: EventCatalogOrigin,
): EventCatalogEntry[] =>
    names.map((name) => ({
        name,
        source,
        origin,
        metadata: METADATA_BY_NAME[name] ?? null,
    }));

const buildPersistedUserEventCatalogEntries = (): EventCatalogEntry[] =>
    Object.values(PersistedUserEvent).map((name) => ({
        name,
        source: 'user',
        origin: 'backend',
        metadata: {
            ...userEventRequestContext,
            ...(USER_EVENT_EXTRA[name] ?? {}),
        },
    }));

export const listEventCatalog = (): EventCatalogEntry[] => [
    ...toEventCatalogEntries(
        Object.values(AnalyticsUserEvent),
        'user',
        'member-app',
    ),
    ...toEventCatalogEntries(
        Object.values(AnalyticsMembershipEvent),
        'membership',
        'member-app',
    ),
    ...toEventCatalogEntries(
        Object.values(BackendUserEvent),
        'user',
        'backend',
    ),
    ...toEventCatalogEntries(
        Object.values(BackendMembershipEvent),
        'membership',
        'backend',
    ),
    ...buildPersistedUserEventCatalogEntries(),
];
